import React, { useMemo, useEffect } from 'react';
import { BrowserRouter, useSearchParams, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { PortalAuthProvider, usePortalAuth } from './context/PortalAuthContext';
import { AppRoutes } from './routes';
import { createPortalTheme, defaultTheme } from './styles/theme';
import LanguageSync from './i18n/LanguageSync';

// Dynamic theme wrapper that uses account colors
const ThemedApp: React.FC = () => {
  const { account } = usePortalAuth();

  const theme = useMemo(() => {
    if (account?.primaryColor) {
      return createPortalTheme(account.primaryColor, account.secondaryColor);
    }
    return defaultTheme;
  }, [account?.primaryColor, account?.secondaryColor]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppRoutes />
    </ThemeProvider>
  );
};

// Token handler that checks URL for token and redirects to auth
const TokenHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { authenticate, isAuthenticated, isLoading } = usePortalAuth();

  useEffect(() => {
    // TokenLanding verifies /auth links and performs their requested redirect.
    if (pathname === '/auth') return;
    const token = searchParams.get('token');
    if (token && !isAuthenticated && !isLoading) {
      // If we have a token in URL, authenticate with it
      authenticate(token);
    }
  }, [pathname, searchParams, authenticate, isAuthenticated, isLoading]);

  return <>{children}</>;
};

function App() {
  return (
    <BrowserRouter basename="/portal">
      <PortalAuthProvider>
        <LanguageSync />
        <TokenHandler>
          <ThemedApp />
        </TokenHandler>
      </PortalAuthProvider>
    </BrowserRouter>
  );
}

export default App;
